import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiShield, FiBriefcase, FiLogOut, FiKey, FiImage, FiMenu } from 'react-icons/fi';
import { clearSession, getRole, getHrCompany, setHrCompanyLogo } from '../api/auth';
import { uploadHrCompanyLogo } from '../api/b2b';
import HRChangePassword from './HRChangePassword';

const dashboards = [
  { key: 'superadmin', label: 'Super Admin', icon: <FiShield /> },
  { key: 'b2b', label: 'B2B Company', icon: <FiBriefcase /> },
];

// Role is chosen only on the login page — there is no in-app role switcher.
// To switch role you log out (which returns to the login page's role dropdown).
function Navbar({ dashboard, onToggleSidebar }) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [logoBump, setLogoBump] = useState(0); // force avatar re-read after upload
  const profileRef = useRef(null);
  const logoInputRef = useRef(null);
  const navigate = useNavigate();

  const current = dashboards.find((d) => d.key === dashboard);
  const isHr = getRole() === 'hr';
  const hrCompany = getHrCompany();
  const title = dashboard === 'b2b' && hrCompany?.name ? hrCompany.name : `${current?.label} Dashboard`;
  const avatarLogo = isHr ? hrCompany?.logo_url : null;

  useEffect(() => {
    function handleClick(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleLogout = () => {
    clearSession();
    setProfileOpen(false);
    navigate('/login');
  };

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const url = await uploadHrCompanyLogo(file);
      setHrCompanyLogo(url);
      setLogoBump((n) => n + 1);
    } catch (err) {
      alert(err.message || 'Could not update company logo');
    }
  };

  if (showChangePw) {
    return (
      <HRChangePassword
        forced={false}
        onDone={() => setShowChangePw(false)}
        onCancel={() => setShowChangePw(false)}
      />
    );
  }

  return (
    <nav className="navbar">
      <div className="navbar-left">
        <button className="sidebar-toggle" onClick={onToggleSidebar} title="Toggle menu" aria-label="Toggle menu">
          <FiMenu />
        </button>
        {current?.icon}
        <h1 className="navbar-title">{title}</h1>
      </div>
      <div className="navbar-right">
        <div className="profile-menu" ref={profileRef}>
          <button className="admin-avatar" onClick={() => setProfileOpen(!profileOpen)}>
            {avatarLogo ? (
              <img key={logoBump} src={avatarLogo} alt="DP" className="admin-avatar-img" />
            ) : (
              'M'
            )}
          </button>
          {profileOpen && (
            <div className="profile-dropdown">
              {isHr && (
                <>
                  <button
                    className="profile-dropdown-item"
                    onClick={() => {
                      setProfileOpen(false);
                      logoInputRef.current?.click();
                    }}
                  >
                    <FiImage />
                    Update company logo
                  </button>
                  <button
                    className="profile-dropdown-item"
                    onClick={() => {
                      setProfileOpen(false);
                      setShowChangePw(true);
                    }}
                  >
                    <FiKey />
                    Change password
                  </button>
                </>
              )}
              <button className="profile-dropdown-item" onClick={handleLogout}>
                <FiLogOut />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
      <input
        ref={logoInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleLogoFile}
      />
    </nav>
  );
}

export default Navbar;
